<script setup>
import { ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

const emit = defineEmits(['close', 'created']);
const store = useStore();
const { t } = useI18n();

const name = ref('');
const category = ref('sales');
const saving = ref(false);
const error = ref('');

const defaultsBySales = [
  { name: 'Novos Leads', position: 0, column_type: 'default' },
  { name: 'Qualificação', position: 1, column_type: 'default' },
  { name: 'Apresentação', position: 2, column_type: 'default' },
  { name: 'Negociação', position: 3, column_type: 'default' },
  { name: 'Fechamento', position: 4, column_type: 'default' },
  { name: 'Perdidos', position: 5, column_type: 'default' },
];

const defaultsByPostSales = [
  { name: 'Novos Clientes', position: 0, column_type: 'default' },
  { name: 'Aguardando', position: 1, column_type: 'default' },
  { name: 'Onboarding', position: 2, column_type: 'default' },
  { name: 'Gestão', position: 3, column_type: 'default' },
  { name: 'Finalizado', position: 4, column_type: 'default' },
  { name: 'Cancelados', position: 5, column_type: 'default' },
];

const columns = ref(defaultsBySales.map(c => ({ ...c })));

function onCategoryChange(val) {
  category.value = val;
  const defaults = val === 'post_sales' ? defaultsByPostSales : defaultsBySales;
  columns.value = defaults.map(c => ({ ...c }));
}

function addColumn() {
  columns.value.push({
    name: t('KANBAN.SETTINGS.NEW_COLUMN'),
    position: columns.value.length,
    column_type: 'default',
  });
}

function removeColumn(idx) {
  columns.value.splice(idx, 1);
  columns.value.forEach((c, i) => {
    c.position = i;
  });
}

async function save() {
  if (!name.value.trim()) {
    error.value = t('KANBAN.NEW_PIPELINE_MODAL.ERROR_NAME_REQUIRED');
    return;
  }
  saving.value = true;
  error.value = '';
  try {
    await store.dispatch('kanban/createPipeline', {
      name: name.value.trim(),
      category: category.value,
      columns: columns.value,
    });
    emit('created');
  } catch (e) {
    error.value = Array.isArray(e)
      ? e.join(', ')
      : t('KANBAN.NEW_PIPELINE_MODAL.ERROR_CREATE');
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
    @click.self="emit('close')"
  >
    <div
      class="bg-n-background rounded-xl shadow-xl w-full md:w-[560px] mx-4 md:mx-0 max-h-[80vh] overflow-y-auto relative"
    >
      <button
        class="absolute top-4 right-4 text-n-slate-9 hover:text-n-slate-12"
        @click="emit('close')"
      >
        <span class="i-lucide-x size-5" />
      </button>

      <div class="p-6">
        <h2 class="text-lg font-semibold text-n-slate-12 mb-5">
          {{ t('KANBAN.NEW_PIPELINE_MODAL.TITLE') }}
        </h2>

        <!-- Nome -->
        <div class="mb-4">
          <label class="block text-xs font-medium text-n-slate-10 mb-1">
            {{ t('KANBAN.NEW_PIPELINE_MODAL.NAME_LABEL') }}
          </label>
          <input
            v-model="name"
            type="text"
            :placeholder="t('KANBAN.NEW_PIPELINE_MODAL.NAME_PLACEHOLDER')"
            class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-transparent text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:outline-none focus:ring-0 focus:border-n-container"
          />
        </div>

        <!-- Categoria -->
        <div class="mb-5">
          <label class="block text-xs font-medium text-n-slate-10 mb-2">
            {{ t('KANBAN.NEW_PIPELINE_MODAL.TYPE_LABEL') }}
          </label>
          <div
            class="flex items-center gap-2 bg-n-surface-2 p-1 rounded-lg border border-n-weak w-fit"
          >
            <button
              class="flex items-center gap-1.5 px-3 py-1 rounded-md text-sm font-medium transition-colors"
              :class="
                category === 'sales'
                  ? 'bg-n-background shadow-sm text-n-slate-12 border border-n-container'
                  : 'text-n-slate-10 hover:text-n-slate-12'
              "
              @click="onCategoryChange('sales')"
            >
              <span class="i-lucide-trending-up size-4 text-n-teal-9" />
              {{ t('KANBAN.NEW_PIPELINE_MODAL.TYPE_SALES') }}
            </button>
            <button
              class="flex items-center gap-1.5 px-3 py-1 rounded-md text-sm font-medium transition-colors"
              :class="
                category === 'post_sales'
                  ? 'bg-n-background shadow-sm text-n-slate-12 border border-n-container'
                  : 'text-n-slate-10 hover:text-n-slate-12'
              "
              @click="onCategoryChange('post_sales')"
            >
              <span class="i-lucide-headset size-4 text-n-amber-10" />
              {{ t('KANBAN.NEW_PIPELINE_MODAL.TYPE_POST_SALES') }}
            </button>
          </div>
        </div>

        <!-- Colunas -->
        <div class="mb-5">
          <label class="block text-xs font-medium text-n-slate-10 mb-2">
            {{ t('KANBAN.NEW_PIPELINE_MODAL.COLUMNS_LABEL') }}
          </label>
          <div class="space-y-1">
            <div
              v-for="(col, idx) in columns"
              :key="idx"
              class="flex items-center gap-2"
            >
              <input
                v-model="col.name"
                type="text"
                class="flex-1 text-sm border border-n-weak rounded-md px-2 py-1 bg-transparent text-n-slate-12 focus:outline-none focus:outline-none focus:ring-0 focus:border-n-container"
              />
              <button
                class="flex-shrink-0 text-n-ruby-9 hover:text-n-ruby-11"
                @click="removeColumn(idx)"
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>
          <button
            class="mt-2 text-sm text-n-brand hover:opacity-80 font-medium"
            @click="addColumn"
          >
            {{ t('KANBAN.NEW_PIPELINE_MODAL.ADD_COLUMN') }}
          </button>
        </div>

        <p v-if="error" class="text-xs text-n-ruby-11 mb-3">{{ error }}</p>

        <div class="flex justify-end gap-2">
          <button
            class="px-4 py-2 text-sm text-n-slate-11 hover:text-n-slate-12 border border-n-weak rounded-lg transition-colors"
            @click="emit('close')"
          >
            {{ t('KANBAN.NEW_PIPELINE_MODAL.CANCEL') }}
          </button>
          <button
            class="px-4 py-2 text-sm bg-n-brand text-white rounded-lg hover:brightness-110 transition-all disabled:opacity-50"
            :disabled="saving"
            @click="save"
          >
            {{
              saving
                ? t('KANBAN.NEW_PIPELINE_MODAL.SAVING')
                : t('KANBAN.NEW_PIPELINE_MODAL.SAVE')
            }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
